import { UpdateGDriveFolderPayload } from 'app/modules/orgSettings/requests';

import { sendErrorToast, sendSuccessToast } from 'app/shared/toasts/actions';
import { u21CreateAsyncThunk } from 'app/shared/thunk/u21CreateAsyncThunk';
import { u21CreateSlice } from 'app/shared/thunk/u21CreateSlice';
import { updateGDriveFolder } from 'app/modules/orgSettings/api';
import { OrgFeature } from 'app/modules/orgManagement/models';
import { SarConfig } from 'app/modules/fincenSARSettings/responses';
import {
  AlertConfig,
  ConsortiumConfig,
  LumosConfig,
  OrgScenarioConfig,
  QueryBuilderConfig,
  RuleExecutionConfig,
  StorageConfig,
} from 'app/modules/orgSettings/models';
import {
  DEFAULT_ORG_TAG_LIMIT,
  INITIAL_ALERT_CONFIG,
  INITIAL_CURRENCY_CONFIG,
  INITIAL_FINCEN_314A_SCAN_CONFIG,
  INITIAL_LUMOS_CONFIG,
  INITIAL_ORG_ENUMS,
  INITIAL_QUERY_BUILDER_CONFIG,
  INITIAL_REAL_TIME_RULES_CONFIG,
  INITIAL_RULE_EXECUTION_CONFIG,
  INITIAL_SAR_CONFIG,
  INITIAL_SCENARIO_CONFIG,
} from 'app/modules/orgSettings/constants';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  CurrencyConfig,
  OrgEnums,
  OrgSettingsResponse,
  StorageResource,
  WatchlistConfig,
} from 'app/modules/orgSettings/responses';
import { WatchlistProviders } from 'app/modules/detectionModels/components/scenarioWidgets/watchlist/enums';
import { consoleError } from 'app/shared/utils/console';
import {
  ChainalysisKYTConfig,
  CTRGenerationConfig,
  EnrichmentConfig,
  RealTimeRulesConfig,
} from 'app/modules/orgManagement/responses';
import { FinCEN314aScanConfig } from 'app/modules/lists/models';

const ORG_SETTINGS_NAME = 'orgSettings';

const resolveOrgTagLimit = (
  tagLimit: OrgSettingsResponse['tag_limit'],
): number =>
  tagLimit != null && tagLimit > 0 ? tagLimit : DEFAULT_ORG_TAG_LIMIT;

interface OrgSettingsState {
  alertConfig: AlertConfig;
  consortiumConfig: ConsortiumConfig;
  currencyConfig: CurrencyConfig;
  homeCurrencyCode: string;
  lumosConfig: LumosConfig;
  orgFeatures: OrgFeature[];
  orgEnums: OrgEnums;
  queryBuilderConfig: QueryBuilderConfig;
  ruleExecutionConfig: RuleExecutionConfig;
  realTimeRulesConfig: RealTimeRulesConfig;
  ctrGenerationConfig: CTRGenerationConfig;
  sarConfig: SarConfig;
  scenarioConfig: OrgScenarioConfig;
  storageConfig: StorageConfig;
  storageResources: StorageResource[];
  finCEN314aScanConfig: FinCEN314aScanConfig;

  loadingUpdateGDriveFolder: boolean;

  ffipPgpEncryptionEnabled: boolean;

  immutableNotesEnabled: boolean;

  classifiedQueuesEnabled: boolean;

  kytConfig: { chainalysis?: ChainalysisKYTConfig };

  watchlistConfig: WatchlistConfig;

  enrichmentConfig: EnrichmentConfig;

  displayTimezone: string | null;
  tagLimit: number;
}

export const INITIAL_STATE: Readonly<OrgSettingsState> = {
  alertConfig: INITIAL_ALERT_CONFIG,
  consortiumConfig: {},
  currencyConfig: INITIAL_CURRENCY_CONFIG,
  homeCurrencyCode: 'USD',
  lumosConfig: INITIAL_LUMOS_CONFIG,
  orgFeatures: [],
  orgEnums: INITIAL_ORG_ENUMS,
  queryBuilderConfig: { ...INITIAL_QUERY_BUILDER_CONFIG },
  ruleExecutionConfig: INITIAL_RULE_EXECUTION_CONFIG,
  realTimeRulesConfig: INITIAL_REAL_TIME_RULES_CONFIG,
  sarConfig: INITIAL_SAR_CONFIG,
  scenarioConfig: INITIAL_SCENARIO_CONFIG,
  storageConfig: {},
  storageResources: [],
  finCEN314aScanConfig: INITIAL_FINCEN_314A_SCAN_CONFIG,
  ctrGenerationConfig: {},
  loadingUpdateGDriveFolder: false,

  ffipPgpEncryptionEnabled: false,
  immutableNotesEnabled: false,
  classifiedQueuesEnabled: false,
  kytConfig: {},
  watchlistConfig: { watchlist_provider: WatchlistProviders.LEXISNEXIS },
  enrichmentConfig: {},
  displayTimezone: null,
  tagLimit: DEFAULT_ORG_TAG_LIMIT,
};

export const updateGDriveFolderThunk = u21CreateAsyncThunk(
  `${ORG_SETTINGS_NAME}/UPDATE_GDRIVE_FOLDER`,
  async (payload: UpdateGDriveFolderPayload, { dispatch }) => {
    try {
      const response = await updateGDriveFolder(payload);
      dispatch(updateOrgSettings(response));
      dispatch(sendSuccessToast('Successfully updated Google Drive folder.'));
    } catch (e) {
      dispatch(sendErrorToast('Failed to update Google Drive folder.'));
      throw e;
    }
  },
);

const parseJSON = <TJSON = unknown>(str: string, fallback: TJSON): TJSON => {
  try {
    return JSON.parse(str) as TJSON;
  } catch {
    consoleError('Invalid org settings', str);
    return fallback;
  }
};

const orgSettingsSlice = u21CreateSlice({
  name: ORG_SETTINGS_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    updateOrgSettings: (draft, action: PayloadAction<OrgSettingsResponse>) => {
      const alertConfig = parseJSON(
        action.payload.alert_config,
        INITIAL_STATE.alertConfig,
      );
      draft.alertConfig =
        Object.entries(alertConfig).length > 0
          ? alertConfig
          : INITIAL_STATE.alertConfig;

      draft.consortiumConfig = parseJSON(
        action.payload.consortium_config,
        INITIAL_STATE.consortiumConfig,
      );

      draft.currencyConfig =
        action.payload.currency_config !== null &&
        typeof action.payload.currency_config === 'object'
          ? {
              ...INITIAL_CURRENCY_CONFIG,
              ...action.payload.currency_config,
            }
          : INITIAL_CURRENCY_CONFIG;

      draft.homeCurrencyCode = action.payload.home_currency_code;

      draft.lumosConfig = parseJSON(
        action.payload.lumos_config,
        INITIAL_STATE.lumosConfig,
      );

      draft.orgFeatures = action.payload.org_features;

      draft.orgEnums = action.payload.org_enums.enums;

      draft.queryBuilderConfig = parseJSON(
        action.payload.query_builder_config,
        INITIAL_STATE.queryBuilderConfig,
      );

      draft.ruleExecutionConfig = parseJSON(
        action.payload.rule_execution_config,
        INITIAL_STATE.ruleExecutionConfig,
      );

      draft.realTimeRulesConfig = action.payload.real_time_rules_config;

      draft.sarConfig = parseJSON(
        action.payload.sar_config,
        INITIAL_STATE.sarConfig,
      );

      draft.scenarioConfig = parseJSON(
        action.payload.scenario_config,
        INITIAL_STATE.scenarioConfig,
      );

      draft.storageConfig = parseJSON(
        action.payload.storage_config,
        INITIAL_STATE.storageConfig,
      );

      draft.storageResources = action.payload.storage_resources;

      draft.ffipPgpEncryptionEnabled =
        action.payload.ffip_pgp_encryption_enabled;

      draft.immutableNotesEnabled = action.payload.immutable_notes_enabled;

      draft.classifiedQueuesEnabled = action.payload.classified_queues_enabled;

      draft.finCEN314aScanConfig = action.payload.fincen_314a_scan_config;

      draft.kytConfig = action.payload.kyt_config;

      draft.watchlistConfig = action.payload.watchlist_config;

      draft.enrichmentConfig = action.payload.enrichment_config;

      draft.ctrGenerationConfig = action.payload.ctr_generation_config;

      draft.displayTimezone = action.payload.display_timezone ?? null;

      draft.tagLimit = resolveOrgTagLimit(action.payload.tag_limit);
    },
  },
  extraReducers: (builder) => {
    builder.addLoadingCase(
      updateGDriveFolderThunk,
      'loadingUpdateGDriveFolder',
    );
  },
});

export const ORG_SETTINGS_SLICE_NAME = orgSettingsSlice.name;
export const { updateOrgSettings } = orgSettingsSlice.actions;
export default orgSettingsSlice.reducer;
